/**
 * LazyPanel helpers - shared code-splitting wrappers for demo panels.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { Suspense, lazy, type ComponentType } from "react";

export type PanelComponent<Props extends object = Record<string, never>> = ComponentType<Props>;

export interface LazyPanelOptions {
  readonly displayName?: string;
  readonly fallback?: React.ReactNode;
}

export interface DefaultPanelModule<Props extends object = Record<string, never>> {
  readonly default: PanelComponent<Props>;
}

export type LazyNamedPanelLoader<
  ExportName extends string,
  Props extends object = Record<string, never>,
> = () => Promise<Partial<Record<ExportName, PanelComponent<Props>>>>;

export type LazyNamedPanelLoaders = Record<string, LazyNamedPanelLoader<string>>;

export type LazyNamedPanelMap<Loaders extends LazyNamedPanelLoaders> = {
  readonly [ExportName in keyof Loaders]: PanelComponent;
};

function wrapLazyPanel<Props extends object>(
  LazyComponent: React.LazyExoticComponent<PanelComponent<Props>>,
  options: LazyPanelOptions | undefined,
): PanelComponent<Props> {
  const displayName = options?.displayName;
  const fallback = options?.fallback;

  if (fallback === undefined) {
    return LazyComponent;
  }

  function LazyPanelWithFallback(props: Props): React.JSX.Element {
    return <Suspense fallback={fallback}>{React.createElement(LazyComponent, props)}</Suspense>;
  }
  LazyPanelWithFallback.displayName = displayName ?? "LazyPanelWithFallback";
  return LazyPanelWithFallback;
}

export function lazyPanel<Props extends object = Record<string, never>>(
  loader: () => Promise<DefaultPanelModule<Props>>,
  options?: LazyPanelOptions,
): PanelComponent<Props> {
  return wrapLazyPanel(lazy(loader), options);
}

export function lazyNamedPanel<
  ExportName extends string,
  Props extends object = Record<string, never>,
>(
  loader: LazyNamedPanelLoader<ExportName, Props>,
  exportName: ExportName,
  options?: LazyPanelOptions,
): PanelComponent<Props> {
  return lazyPanel(async () => {
    const loaded = await loader();
    const component = loaded[exportName];
    if (component === undefined) {
      const availableExports = Object.keys(loaded).sort();
      throw new Error(
        `lazyNamedPanel could not find export "${exportName}". Available exports: ${
          availableExports.length === 0 ? "none" : availableExports.join(", ")
        }.`,
      );
    }
    return { default: component };
  }, options);
}

export function lazyNamedPanels<const Loaders extends LazyNamedPanelLoaders>(
  loaders: Loaders,
  options?: LazyPanelOptions,
): LazyNamedPanelMap<Loaders> {
  return Object.fromEntries(
    Object.entries(loaders).map(([exportName, loader]) => [
      exportName,
      lazyNamedPanel(loader, exportName, options),
    ]),
  ) as LazyNamedPanelMap<Loaders>;
}
